import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  moduleOfferAuthoringApi,
  type ModuleOfferPageParams,
} from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type {
  CommercialOfferDraft,
  CommercialOfferDraftPage,
  CommercialOfferDraftReplace,
  CommercialOfferDraftWrite,
  CommercialOfferPublicationReview,
  CommercialOfferOwnerView,
  CommercialPublicationRequest,
} from "@bw/shared/types/generated";

/** Required actual Admin proof belongs to the backend; source absence is not approval. */
export function useModuleOfferDrafts(
  page?: ModuleOfferPageParams,
): UseQueryResult<CommercialOfferDraftPage, Error> {
  const { user, loading } = useAuth();
  return useQuery({
    queryKey: ["module-offer-drafts", user?.uid, page?.limit, page?.offset],
    queryFn: () => moduleOfferAuthoringApi.listDrafts(page),
    enabled: !loading && Boolean(user?.uid),
    retry: false,
  });
}
export function useModuleOfferDraft(
  draftId: string | undefined,
): UseQueryResult<CommercialOfferDraft, Error> {
  const { user, loading } = useAuth();
  return useQuery({
    queryKey: ["module-offer-draft", user?.uid, draftId],
    queryFn: () => moduleOfferAuthoringApi.getDraft(draftId!),
    enabled: !loading && Boolean(user?.uid && draftId),
    retry: false,
  });
}
export function useCreateModuleOfferDraft(): UseMutationResult<
  CommercialOfferDraft,
  Error,
  CommercialOfferDraftWrite
> {
  const { user } = useAuth();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: CommercialOfferDraftWrite) =>
      moduleOfferAuthoringApi.createDraft(data),
    retry: false,
    onSuccess: async () => {
      await client.invalidateQueries({
        queryKey: ["module-offer-drafts", user?.uid],
      });
    },
  });
}
export function useReplaceModuleOfferDraft(
  draftId: string | undefined,
): UseMutationResult<CommercialOfferDraft, Error, CommercialOfferDraftReplace> {
  const { user } = useAuth();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: CommercialOfferDraftReplace) =>
      moduleOfferAuthoringApi.replaceDraft(draftId!, data),
    retry: false,
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({
          queryKey: ["module-offer-drafts", user?.uid],
        }),
        client.invalidateQueries({
          queryKey: ["module-offer-draft", user?.uid, draftId],
        }),
        client.invalidateQueries({
          queryKey: ["module-offer-publication", user?.uid, draftId],
        }),
      ]);
    },
  });
}
export function useModuleOfferPublicationReview(
  draftId: string | undefined,
  environment: CommercialPublicationRequest["environment"],
): UseQueryResult<CommercialOfferPublicationReview, Error> {
  const { user, loading } = useAuth();
  return useQuery({
    queryKey: ["module-offer-publication", user?.uid, draftId, environment],
    queryFn: () =>
      moduleOfferAuthoringApi.getPublicationReview(draftId!, environment),
    enabled: !loading && Boolean(user?.uid && draftId),
    retry: false,
  });
}
export function usePublishModuleOffer(
  draftId: string | undefined,
): UseMutationResult<
  CommercialOfferOwnerView,
  Error,
  CommercialPublicationRequest
> {
  return useMutation({
    mutationFn: (data: CommercialPublicationRequest) =>
      moduleOfferAuthoringApi.publish(draftId!, data),
    retry: false,
  });
}
export function useModuleOfferVersion(
  versionId: string | undefined,
): UseQueryResult<CommercialOfferOwnerView, Error> {
  const { user, loading } = useAuth();
  return useQuery({
    queryKey: ["module-offer-version", user?.uid, versionId],
    queryFn: () => moduleOfferAuthoringApi.getVersion(versionId!),
    enabled: !loading && Boolean(user?.uid && versionId),
    retry: false,
  });
}
